// src/components/NavBar.js

import React, { useState, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { updateTL } from '../../redux/slices/userSlice';
import { Box, Button, Tooltip } from '@mui/material';
import NotificationCard from './notification';
import TinkerLabListAPIs from '../../utilities/api/TinkersLab/TinkerLabListAPIs';
import CreateTL from './CreateTL';
import { ImageBaseURL } from '../../utilities/api/axios';
import DropdownArrow from './icons/DropdownArrow';
import AlertNotificationIcon from './icons/AlertNotificationIcon';
import CalendarIcon from './icons/CalendarIcon';
import ReportIcon from './icons/ReportIcon';
import MessagesIcon from './icons/MessagesIcon';
import MenuIcon from '@mui/icons-material/Menu';
import ReportButton from '../reportButton/reportButton';

const NavBar = ({ isMobile, handleDrawerToggle }) => {
  const [notifi, setNotifi] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [openNotifications, setOpenNotifications] = useState(false);
  const [tinkerLabs, setTinkerLabs] = useState([]);
  const [openCreateTL, setOpenCreateTL] = useState(false);

  const { access, user } = useSelector(state => state.user.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userAvatarUrl = user?.profile_pic
    ? (user.profile_pic.startsWith('http') ? user.profile_pic : `${ImageBaseURL}${user.profile_pic}`)
    : '';

  // Fetch Tinker Labs
  useEffect(() => {
    const fetchTinkerLabs = async () => {
      try {
        const labs = await TinkerLabListAPIs.TinkerLabsListGet();
        const sortedLabList = labs.sort((a, b) => a.id - b.id);
        setTinkerLabs(sortedLabList);
      } catch (error) {
        console.error('Error fetching Tinker Labs data:', error);
      }
    };
    fetchTinkerLabs();
  }, [openCreateTL]);

  const handleTLChange = event => {
    const selectedTLName = event.target.value;
    const selectedLab = tinkerLabs.find(lab => lab.name === selectedTLName);

    if (selectedLab) {
      dispatch(
        updateTL({
          TL: selectedTLName,
          TL_id: selectedLab.id,
          TL_obj: { id: selectedLab.id, name: selectedLab.name },
        })
      );
    } else {
      dispatch(updateTL({ TL: selectedTLName }));
    }
  };

  const handleCreateTLOpen = () => {
    setOpenCreateTL(true);
  };

  const handleCreateTLClose = () => {
    setOpenCreateTL(false);
  };

  const handleNavigation = route => {
    navigate(`/${route}`);
  };

  const showProfileIcon =
    (user?.is_org_admin && user?.TL_name === 'MBF Admin') ||
    !user?.is_org_admin;

  return (
    <>
      <AppBar
        position="sticky"
        color="inherit"
        elevation={1}
        sx={{
          boxShadow: '0px 4px 12px #0000001A',
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 56, sm: 64 },
            px: { xs: 1.5, sm: 2 },
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {/* Drawer Toggle (Mobile Only) */}
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 0.5 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* TL Name / Select */}
          <Box
            sx={{
              flexShrink: 1,
              minWidth: 0,
              maxWidth: { xs: '50%', sm: '60%', md: 'auto' },
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {user?.is_org_admin ? (
              <Select
                IconComponent={DropdownArrow}
                value={user?.TL_name || ''}
                onChange={handleTLChange}
                variant="standard"
                disableUnderline
                sx={{
                  color: '#007BFF',
                  fontWeight: 600,
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  maxWidth: '100%',
                  '& .MuiSelect-select': {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    '&:focus': {
                      backgroundColor: 'transparent',
                    },
                  },
                }}
                MenuProps={{
                  disableScrollLock: true,
                }}
              >
                {tinkerLabs.map(lab => (
                  <MenuItem key={lab.id} value={lab.name}>
                    {lab.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography
                sx={{
                  color: '#007BFF',
                  fontWeight: 600,
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {user?.TL_name}
              </Typography>
            )}
          </Box>

          {/* Spacer */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Right Side Actions */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1 },
            }}
          >
            {user?.TL_name === 'MBF Admin' && (
              <Button
                variant="contained"
                onClick={handleCreateTLOpen}
                sx={{
                  display: 'inline-flex',
                  backgroundColor: '#007BFF',
                  borderRadius: '10px',
                  fontSize: '0.75rem',
                  px: 1.5,
                  py: 0.5,
                  minWidth: '35px'
                }}
              >
                {isMobile ? "+" : "Create TL"}
              </Button>
            )}

            <Tooltip title="Notifications">
              <IconButton
                size="small"
                onClick={() => {
                  setOpenNotifications(!openNotifications);
                  if (notifi && !openNotifications) {
                    setNotifi(false);
                  }
                }}
              >
                <AlertNotificationIcon notifi={notifi} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Report Issue">
              <Box sx={{ display: 'inline-flex' }}>
                <ReportButton displayMode="navbar" icon={ReportIcon} />
              </Box>
            </Tooltip>

            <Tooltip title="Calendar">
              <IconButton
                size="small"
                onClick={() => handleNavigation('calendar')}
              >
                <CalendarIcon />
              </IconButton>
            </Tooltip>

            {/* <Tooltip title="Messages">
              <IconButton
                size="small"
                onClick={() => handleNavigation('messages')}
              >
                <MessagesIcon />
              </IconButton>
            </Tooltip> */}

            {showProfileIcon && (
              <IconButton
                size="small"
                onClick={() => handleNavigation('profile')}
              >
                <Avatar
                  alt="User Avatar"
                  src={userAvatarUrl}
                  sx={{
                    width: { xs: 28, sm: 32 },
                    height: { xs: 28, sm: 32 },
                  }}
                />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {openNotifications && (
        <NotificationCard
          notifications={notifications}
          closeNotification={setOpenNotifications}
        />
      )}

      <CreateTL open={openCreateTL} handleClose={handleCreateTLClose} />
    </>
  );
};

export default NavBar;
